import React, { useContext, useEffect, useReducer, useRef, useCallback, useState } from 'react';
import {
	TeasersContext,
	teasersInitialState,
	IFilterValues,
	TCountOfTeasers,
	TTeasersSortBy,
	TTypeOfModalWindow,
} from './teasersContext';
import { teasersReducer } from './teasersReducer';
import { TeasersActionTypes } from './teasersActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedTeasers, deleteTeaser } from 'backend/teasers';
import { ITeasers, IGetPaginatedTeasers, TTeaserContentTypes } from 'types';
import { AxiosResponse } from 'axios';

const TeasersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(teasersReducer, teasersInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<ITeasers>, IGetPaginatedTeasers>(
		isMounted.current ? getPaginatedTeasers : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			contentType: state.IDSelectedContentType,
			sortByCustomer: state.sortBy === 'CustomerA-Z' || state.sortBy === 'CustomerZ-A' ? state.sortBy : null,
			sortByContentType:
				state.sortBy === 'ContentTypeA-Z' || state.sortBy === 'ContentTypeZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: TeasersActionTypes.SET_TEASERS,
				payload: {
					teasers: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteTeaser(state.teaserIDForDeletion);
			const filteredTeasers = state.teasers.teasers.filter((el) => el.id !== state.teaserIDForDeletion);
			dispatch({
				type: TeasersActionTypes.SET_TEASERS,
				payload: {
					teasers: {
						teasersCount: state.teasers.teasersCount - 1,
						teasers: filteredTeasers,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: TeasersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.teaserIDForDeletion, state.teasers]);

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: TeasersActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: TeasersActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setIDContentType = (value: Nullable<TTeaserContentTypes>) => {
		dispatch({
			type: TeasersActionTypes.SET_ID_CONTENT_TYPE,
			payload: {
				IDSelectedContentType: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: TeasersActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (value: TTeasersSortBy) => {
		dispatch({
			type: TeasersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: value,
			},
		});
	};

	const setCountOfItems = (value: TCountOfTeasers) => {
		dispatch({
			type: TeasersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: value,
			},
		});
	};

	const changeCurrentPage = (value: number) => {
		dispatch({
			type: TeasersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: value,
			},
		});
	};

	const setTeaserIDForDeletion = (value: string) => {
		dispatch({
			type: TeasersActionTypes.SET_TEASER_ID_FOR_DELETION,
			payload: {
				teaserIDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setTeaserIDForFullInfo = (value: string) => {
		dispatch({
			type: TeasersActionTypes.SET_TEASER_ID_FOR_FULL_INFO,
			payload: {
				teaserIDForFullInfo: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: TeasersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: TeasersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	// fix no content on listing page. The searchString is everywhere and it needed to be added here.
	useEffect(() => {
		if (allowChangingSearchString) {
			dispatch({
				type: TeasersActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: '',
					currentPage: state.currentPage,
				},
			});
		}
	}, [allowChangingSearchString]);
	// fix no content on listing page. The searchString is everywhere and it needed to be added here.

	return (
		<TeasersContext.Provider
			value={{
				deleteSelectedItem,
				setIDCustomer,
				setIDRestaurant,
				setIDContentType,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setTeaserIDForDeletion,
				setTeaserIDForFullInfo,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				teasers: state.teasers,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedContentType: state.IDSelectedContentType,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				teaserIDForDeletion: state.teaserIDForDeletion,
				teaserIDForFullInfo: state.teaserIDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</TeasersContext.Provider>
	);
};

export default TeasersState;
